/**
 * Écran du Diagnostic SEO.
 *
 * **Les familles viennent des données, jamais d'une liste écrite ici.** Le diagnostic Google Ads
 * code ses quatre familles en dur à quatre endroits différents ; ajouter une famille de contrôles
 * SEO ne demandera aucune modification de cet écran.
 *
 * Trois partis pris d'affichage :
 *   - les constats sont ordonnés par gravité puis par trafic en jeu, parce que c'est l'ordre dans
 *     lequel on agit ;
 *   - le journal de collecte est montré, pas caché : un rapport qui ne dit pas qu'il n'a examiné
 *     que cinquante pages sur mille laisse croire à une couverture complète ;
 *   - ce que l'outil ne peut pas vérifier est écrit noir sur blanc, plutôt que passé sous silence.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Search, AlertCircle, AlertTriangle, CheckCircle, MinusCircle, RefreshCw, Loader,
  ChevronRight, ChevronDown, ExternalLink, Info, Link2
} from 'lucide-react';
import config from '../config';

const API_URL = config.API_URL;

/** Apparence de chaque statut. L'ordre du tableau est celui dans lequel on lit les constats. */
const STATUTS = {
  RED: { icone: AlertCircle, couleur: '#dc2626', fond: '#fef2f2', libelle: 'À corriger' },
  ORANGE: { icone: AlertTriangle, couleur: '#b45309', fond: '#fffbeb', libelle: 'À regarder' },
  SKIPPED: { icone: MinusCircle, couleur: '#9ca3af', fond: '#f9fafb', libelle: 'Non évalué' },
  GREEN: { icone: CheckCircle, couleur: '#059669', fond: '#f0fdf4', libelle: 'Conforme' }
};

const LIBELLES_DE_FAMILLE = {
  indexation: 'Indexation',
  technique: 'Technique',
  contenu: 'Contenu',
  requetes: 'Rendement des requêtes',
  performance: 'Performance et mobile'
};

const entier = new Intl.NumberFormat('fr-FR');
const dateHeure = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—');

const styles = {
  carte: { background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '18px' },
  bouton: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 500 },
  principal: { background: '#065f46', color: '#fff' },
  secondaire: { background: '#f3f4f6', color: '#374151' },
  champ: { padding: '9px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', minWidth: '280px' }
};

/** L'anneau de score. Les seuils reprennent ceux du diagnostic Google Ads, pour ne pas dérouter. */
function Anneau({ score }) {
  const couleur = score < 50 ? '#dc2626' : score < 80 ? '#b45309' : '#059669';
  const rayon = 42;
  const circonference = 2 * Math.PI * rayon;

  return (
    <div style={{ position: 'relative', width: 104, height: 104, flexShrink: 0 }}>
      <svg width="104" height="104" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="52" cy="52" r={rayon} fill="none" stroke="#f1f5f9" strokeWidth="8" />
        <circle
          cx="52" cy="52" r={rayon} fill="none" stroke={couleur} strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circonference}
          strokeDashoffset={circonference * (1 - score / 100)}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: '1.75rem', fontWeight: 700, color: couleur, lineHeight: 1 }}>{score}</span>
        <span style={{ fontSize: '0.6875rem', color: '#9ca3af' }}>/ 100</span>
      </div>
    </div>
  );
}

/** Un constat, avec sa recommandation et les éléments concernés. */
function Constat({ controle }) {
  const [ouvert, setOuvert] = useState(false);
  const apparence = STATUTS[controle.status] || STATUTS.SKIPPED;
  const Icone = apparence.icone;
  const elements = controle.affectedItems || [];
  const enEchec = controle.status === 'RED' || controle.status === 'ORANGE';

  return (
    <div style={{ borderTop: '1px solid #f1f5f9', padding: '12px 0' }}>
      <div
        onClick={() => elements.length > 0 && setOuvert(!ouvert)}
        style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: elements.length > 0 ? 'pointer' : 'default' }}
      >
        <Icone size={17} style={{ color: apparence.couleur, flexShrink: 0, marginTop: '2px' }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: '0.9375rem' }}>{controle.label}</strong>
            {controle.severity === 'CRITICAL' && enEchec && (
              <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: '#dc2626', background: '#fef2f2', padding: '2px 7px', borderRadius: '4px' }}>
                CRITIQUE
              </span>
            )}
            {controle.periodLabel && (
              <span style={{ fontSize: '0.6875rem', color: '#9ca3af' }}>{controle.periodLabel}</span>
            )}
          </div>
          {controle.summary && (
            <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: '#4b5563', lineHeight: 1.5 }}>
              {controle.summary}
            </p>
          )}
          {/* La recommandation ne s'affiche que s'il y a quelque chose à faire : la répéter sur un
              contrôle conforme noierait celles qui comptent. */}
          {enEchec && controle.recommendation && (
            <p style={{ margin: '6px 0 0', fontSize: '0.8125rem', color: '#065f46', background: '#f0fdf4', padding: '8px 10px', borderRadius: '6px', lineHeight: 1.5 }}>
              {controle.recommendation}
            </p>
          )}
        </div>
        {elements.length > 0 && (
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem', color: '#6b7280', flexShrink: 0 }}>
            {elements.length}
            {ouvert ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>
        )}
      </div>

      {ouvert && elements.length > 0 && (
        <div style={{ marginTop: '10px', marginLeft: '27px', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <tbody>
              {elements.map((element, i) => (
                <tr key={`${element.id}-${i}`} style={{ borderTop: '1px solid #f8fafc' }}>
                  <td style={{ padding: '5px 8px', maxWidth: '380px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {element.url ? (
                      <a href={element.url} target="_blank" rel="nofollow noopener noreferrer" title={element.name}
                        style={{ color: '#374151', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        {String(element.name).replace(/^https?:\/\/[^/]+/, '') || '/'}
                        <ExternalLink size={11} style={{ opacity: 0.5, flexShrink: 0 }} />
                      </a>
                    ) : element.name}
                  </td>
                  <td style={{ padding: '5px 8px', color: '#6b7280', whiteSpace: 'nowrap' }}>{element.value || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DiagnosticSeoView({ token, selectedClientId }) {
  const [proprietes, setProprietes] = useState([]);
  const [propriete, setPropriete] = useState('');
  const [scan, setScan] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [progression, setProgression] = useState(null);
  const [erreur, setErreur] = useState('');
  const [erreursDeCollecte, setErreursDeCollecte] = useState([]);
  const [famillesFermees, setFamillesFermees] = useState({});
  const [conformesVisibles, setConformesVisibles] = useState({});

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const base = `${API_URL}/api/tools/diagnostic-seo`;

  const chargerProprietes = useCallback(async () => {
    try {
      setErreur('');
      const params = selectedClientId ? `?client=${selectedClientId}` : '';
      const r = await axios.get(`${base}/properties${params}`, { headers });
      const liste = r.data.data || [];
      setProprietes(liste);
      // Une seule propriété : on la choisit, personne n'a envie de cliquer pour confirmer l'évidence.
      if (liste.length === 1) setPropriete(liste[0].id);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Impossible de charger les propriétés Search Console');
    } finally {
      setChargement(false);
    }
  }, [selectedClientId, headers]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { chargerProprietes(); }, [chargerProprietes]);

  const chargerDernier = useCallback(async () => {
    if (!propriete) { setScan(null); return; }
    setChargement(true);
    setErreur('');
    try {
      const [dernier, hist] = await Promise.all([
        axios.get(`${base}/${propriete}/latest`, { headers }),
        axios.get(`${base}/${propriete}/history?limit=20`, { headers })
      ]);
      setScan(dernier.data.data || null);
      setHistorique(hist.data.data || []);
      setErreursDeCollecte([]);
    } catch (e) {
      setErreur(e.response?.data?.error || e.response?.data?.message || 'Chargement impossible');
      setScan(null);
    } finally {
      setChargement(false);
    }
  }, [propriete, headers]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { chargerDernier(); }, [chargerDernier]);

  /**
   * Lance un scan en flux.
   *
   * `EventSource` ne permet pas d'en-tête : le jeton passe donc en paramètre d'URL, comme pour le
   * scan Google Ads. Le flux est fermé dans tous les cas de sortie, sinon la connexion resterait
   * ouverte côté serveur à continuer de crawler.
   */
  const lancerScan = () => {
    if (!propriete || enCours) return;
    setEnCours(true);
    setErreur('');
    setProgression({ libelle: 'Démarrage…' });

    const flux = new EventSource(`${base}/${propriete}/scan/stream?token=${encodeURIComponent(token)}`);

    flux.addEventListener('progress', (e) => {
      const d = JSON.parse(e.data);
      if (d.type === 'collecte') setProgression({ libelle: d.libelle });
      else if (d.type === 'scan_info') setProgression({ libelle: 'Évaluation des contrôles', total: d.total, fait: 0 });
      else if (d.type === 'check_complete') setProgression(p => ({ ...p, libelle: 'Évaluation des contrôles', fait: d.current, total: d.total }));
    });

    flux.addEventListener('complete', (e) => {
      const d = JSON.parse(e.data);
      setScan(d.data);
      setErreursDeCollecte(d.erreursDeCollecte || []);
      setEnCours(false);
      setProgression(null);
      flux.close();
      axios.get(`${base}/${propriete}/history?limit=20`, { headers })
        .then(r => setHistorique(r.data.data || []))
        .catch(() => {});
    });

    flux.addEventListener('error', (e) => {
      let message = 'Le scan a échoué';
      try { message = JSON.parse(e.data)?.message || message; } catch { /* flux coupé sans charge utile */ }
      setErreur(message);
      setEnCours(false);
      setProgression(null);
      flux.close();
    });

    flux.onerror = () => {
      // Une coupure de transport n'a pas de charge utile : on ne peut que le dire honnêtement.
      if (enCours) setErreur('Connexion au flux de scan interrompue');
      setEnCours(false);
      setProgression(null);
      flux.close();
    };
  };

  const proprieteChoisie = proprietes.find(p => p.id === propriete);

  // ---------- états d'attente et d'absence ----------

  if (chargement && proprietes.length === 0) {
    return <div style={{ ...styles.carte, textAlign: 'center', padding: '48px' }}><Loader size={22} className="spin" /></div>;
  }

  const entete = (
    <div style={{ marginBottom: '18px' }}>
      <h2 style={{ margin: 0, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '9px' }}>
        <Search size={22} /> Diagnostic SEO
      </h2>
      <p style={{ margin: '6px 0 0', fontSize: '0.875rem', color: '#6b7280', maxWidth: '760px' }}>
        Trente-trois contrôles sur une propriété Search Console vérifiée : indexation, santé
        technique, éléments de page, rendement des requêtes et performance mesurée. Chaque constat
        porte le trafic qu'il met en jeu, pour se lire par ordre d'urgence réelle.
      </p>
    </div>
  );

  if (proprietes.length === 0) {
    return (
      <div>
        {entete}
        <div style={{ ...styles.carte, textAlign: 'center', padding: '44px 20px', color: '#6b7280' }}>
          <Link2 size={30} style={{ opacity: 0.4 }} />
          <p style={{ marginTop: '12px', marginBottom: '6px' }}>Aucune propriété Search Console dans votre bibliothèque.</p>
          <p style={{ margin: 0, fontSize: '0.8125rem' }}>
            Le diagnostic ne fonctionne que sur des sites dont vous avez prouvé la propriété dans
            Search Console. Connectez le compte, puis ajoutez les propriétés depuis la bibliothèque.
          </p>
          <button
            onClick={() => { window.location.href = `${API_URL}/api/auth/google-search-console?token=${encodeURIComponent(token)}`; }}
            style={{ ...styles.bouton, ...styles.principal, marginTop: '18px' }}
          >
            Connecter Google Search Console
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {entete}

      <div style={{ ...styles.carte, display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={propriete} onChange={(e) => setPropriete(e.target.value)} style={styles.champ} disabled={enCours}>
          <option value="">Choisir une propriété…</option>
          {proprietes.map(p => (
            <option key={p.id} value={p.id} disabled={!p.pretePourScan}>
              {p.siteUrl}{p.pretePourScan ? '' : ' — reconnexion nécessaire'}
            </option>
          ))}
        </select>

        <button
          onClick={lancerScan}
          disabled={!propriete || enCours || !proprieteChoisie?.pretePourScan}
          style={{ ...styles.bouton, ...styles.principal, opacity: (!propriete || enCours || !proprieteChoisie?.pretePourScan) ? 0.5 : 1 }}
        >
          {enCours ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />}
          {scan ? 'Relancer le diagnostic' : 'Lancer le diagnostic'}
        </button>

        {scan && !enCours && (
          <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
            Dernier scan {dateHeure(scan.scannedAt)}
            {scan.triggeredBy === 'cron' ? ' (automatique)' : ''}
          </span>
        )}
      </div>

      {enCours && progression && (
        <div style={{ ...styles.carte, display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Loader size={18} className="spin" style={{ color: '#065f46', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.875rem', color: '#374151' }}>
              {progression.libelle}
              {progression.total ? ` — ${progression.fait || 0} / ${progression.total}` : ''}
            </div>
            {progression.total > 0 && (
              <div style={{ height: '5px', background: '#f1f5f9', borderRadius: '3px', marginTop: '7px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.round(100 * (progression.fait || 0) / progression.total)}%`, background: '#065f46', transition: 'width .2s' }} />
              </div>
            )}
          </div>
        </div>
      )}

      {erreur && (
        <div style={{ ...styles.carte, background: '#fef2f2', display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '14px 16px' }}>
          <AlertCircle size={18} style={{ color: '#b91c1c', flexShrink: 0, marginTop: '1px' }} />
          <span style={{ fontSize: '0.875rem', color: '#b91c1c' }}>{erreur}</span>
        </div>
      )}

      {!scan && !chargement && !enCours && propriete && (
        <div style={{ ...styles.carte, textAlign: 'center', padding: '40px 20px', color: '#6b7280' }}>
          <p style={{ margin: 0 }}>Cette propriété n'a pas encore été diagnostiquée.</p>
          <p style={{ margin: '8px 0 0', fontSize: '0.8125rem' }}>
            Le diagnostic n'engage aucun coût : Search Console et PageSpeed Insights sont gratuits, et
            les pages sont lues directement. Comptez une à deux minutes.
          </p>
        </div>
      )}

      {scan && (
        <>
          <div style={{ ...styles.carte, display: 'flex', gap: '22px', alignItems: 'center', flexWrap: 'wrap' }}>
            <Anneau score={scan.scoreGlobal} />
            <div style={{ flex: 1, minWidth: '240px' }}>
              <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap' }}>
                {['RED', 'ORANGE', 'GREEN', 'SKIPPED'].map(statut => {
                  const apparence = STATUTS[statut];
                  const Icone = apparence.icone;
                  return (
                    <div key={statut} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Icone size={15} style={{ color: apparence.couleur }} />
                      <strong style={{ fontSize: '1.0625rem' }}>{scan.compteurs?.[statut] || 0}</strong>
                      <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{apparence.libelle}</span>
                    </div>
                  );
                })}
              </div>
              {/* Le journal de collecte : ce qui a été tronqué, ignoré, ou n'a pas pu être fait. */}
              {(scan.journal?.length > 0 || erreursDeCollecte.length > 0) && (
                <div style={{ marginTop: '14px', padding: '10px 12px', background: '#f9fafb', borderRadius: '7px', fontSize: '0.8125rem', color: '#6b7280' }}>
                  <div style={{ display: 'flex', gap: '7px', alignItems: 'flex-start' }}>
                    <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <div>
                      {(scan.journal || []).map((ligne, i) => <div key={i}>{ligne}</div>)}
                      {erreursDeCollecte.map((e, i) => (
                        <div key={`e${i}`} style={{ color: '#b45309' }}>{e.source} : {e.message}</div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {scan.familles.map(famille => {
            const ferme = famillesFermees[famille.nom];
            const echecs = famille.controles.filter(c => c.status === 'RED' || c.status === 'ORANGE');
            const autres = famille.controles.filter(c => c.status !== 'RED' && c.status !== 'ORANGE');
            const montrerAutres = conformesVisibles[famille.nom];

            return (
              <div key={famille.nom} style={styles.carte}>
                <div
                  onClick={() => setFamillesFermees(f => ({ ...f, [famille.nom]: !f[famille.nom] }))}
                  style={{ display: 'flex', alignItems: 'center', gap: '9px', cursor: 'pointer' }}
                >
                  {ferme ? <ChevronRight size={17} /> : <ChevronDown size={17} />}
                  <h3 style={{ margin: 0, fontSize: '1rem', flex: 1 }}>
                    {LIBELLES_DE_FAMILLE[famille.nom] || famille.nom}
                  </h3>
                  {['RED', 'ORANGE'].map(statut => (famille.compteurs?.[statut] ? (
                    <span key={statut} style={{
                      fontSize: '0.75rem', fontWeight: 600, color: STATUTS[statut].couleur,
                      background: STATUTS[statut].fond, padding: '2px 8px', borderRadius: '11px'
                    }}>
                      {famille.compteurs[statut]}
                    </span>
                  ) : null))}
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                    {famille.controles.length} contrôles
                  </span>
                </div>

                {!ferme && (
                  <div style={{ marginTop: '8px' }}>
                    {echecs.length === 0 && (
                      <p style={{ fontSize: '0.875rem', color: '#059669', margin: '10px 0 0' }}>
                        Rien à corriger dans cette famille.
                      </p>
                    )}
                    {echecs.map(c => <Constat key={c.id} controle={c} />)}

                    {autres.length > 0 && (
                      <>
                        <button
                          onClick={() => setConformesVisibles(v => ({ ...v, [famille.nom]: !v[famille.nom] }))}
                          style={{ ...styles.bouton, ...styles.secondaire, marginTop: '12px', padding: '6px 12px', fontSize: '0.8125rem' }}
                        >
                          {montrerAutres ? 'Masquer' : 'Voir'} les {autres.length} contrôle(s) conformes ou non évalués
                        </button>
                        {montrerAutres && autres.map(c => <Constat key={c.id} controle={c} />)}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/*
            Ce que l'outil ne peut pas vérifier, dit explicitement.
            L'API Search Console n'expose ni le rapport Liens ni les actions manuelles : laisser
            croire à une couverture complète serait le pire défaut d'un outil d'audit.
          */}
          <div style={{ ...styles.carte, background: '#f9fafb' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '0.9375rem', display: 'flex', alignItems: 'center', gap: '7px' }}>
              <Info size={16} /> Ce que ce diagnostic ne couvre pas
            </h3>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.8125rem', color: '#6b7280', lineHeight: 1.7 }}>
              <li>
                <strong>Les actions manuelles.</strong> L'API Search Console ne les expose pas. À
                vérifier dans{' '}
                <a href="https://search.google.com/search-console/manual-actions" target="_blank" rel="noopener noreferrer" style={{ color: '#065f46' }}>
                  Search Console → Actions manuelles
                </a>.
              </li>
              <li>
                <strong>Les backlinks.</strong> Le rapport Liens n'a pas d'API non plus. C'est l'outil
                Backlinks qui couvre ce terrain.
              </li>
              <li>
                <strong>Le contenu injecté par JavaScript.</strong> Les pages sont lues en HTML brut,
                sans exécution de script : ce qu'une application monopage ajoute côté client n'est pas vu.
              </li>
              <li>
                <strong>Les données Search Console ont deux à trois jours de retard</strong>, et
                l'historique s'arrête à seize mois.
              </li>
            </ul>
          </div>

          {historique.length > 1 && (
            <div style={styles.carte}>
              <h3 style={{ margin: '0 0 12px', fontSize: '0.9375rem' }}>Scans précédents</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    {['Date', 'Score', 'Déclenchement'].map(t => (
                      <th key={t} style={{ textAlign: 'left', padding: '8px 10px', color: '#065f46', fontWeight: 600 }}>{t}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {historique.map(h => (
                    <tr key={h.id} style={{ borderTop: '1px solid #f1f5f9', background: h.id === scan.id ? '#f0fdf4' : 'transparent' }}>
                      <td style={{ padding: '6px 10px' }}>{dateHeure(h.scannedAt)}</td>
                      <td style={{ padding: '6px 10px' }}>{entier.format(h.scoreGlobal)} / 100</td>
                      <td style={{ padding: '6px 10px', color: '#6b7280' }}>
                        {h.triggeredBy === 'cron' ? 'automatique' : 'manuel'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default DiagnosticSeoView;
